import Phaser from 'phaser';
import { createRoot } from 'react-dom/client';
import { StrictMode } from 'react';
import { AppRoot } from '@/presentation/AppRoot';
import { BootScene } from '@/runtime/scenes/BootScene';
import { logger } from '@/infra/logger/Logger';
import { useStore } from '@/infra/store';
import { getLocalStore } from '@/infra/persistence/LocalStore';
import { loadSnapshot } from '@/services/save';
import { mkCreature } from '@/domain/creature/factory';
import { randomGenome } from '@/domain/genetics/random';
import { uuid } from '@/infra/util/uuid';
import { catchUp } from '@/services/offline-catchup';
import { getCareTickService } from '@/services/care-tick';
import { getNpcLifeService } from '@/services/npc-life';
import { getWorldService } from '@/services/world';
import { getWorldEvolutionService } from '@/services/world-evolution';
import { getAudioService } from '@/services/audio';
import { getCloudSyncService } from '@/services/cloud-sync';
import '@/services/world-events';
import '@/services/combat-events';
import '@/services/cloud-events';
import { detectLocaleFromBrowser } from '@/infra/i18n';
import type { Settings } from '@/domain/settings/types';
import type { Creature } from '@/domain/creature/types';

const SETTINGS_KEY = 'himotchi.settings';

window.addEventListener('error', (ev) => {
  logger.error(
    'window.onerror',
    ev.error instanceof Error ? ev.error : new Error(String(ev.message)),
    { src: ev.filename, line: ev.lineno, col: ev.colno },
  );
});

window.addEventListener('unhandledrejection', (ev) => {
  const err = ev.reason instanceof Error ? ev.reason : new Error(String(ev.reason));
  logger.error('window.unhandledrejection', err);
});

function hydrateSettingsFromLocal(): void {
  const stored = getLocalStore().get<Partial<Settings>>(SETTINGS_KEY);
  if (stored) {
    useStore.getState().hydrateSettings(stored);
  } else {
    // Detecta do navegador na primeira visita
    const browserLocale = detectLocaleFromBrowser();
    useStore.getState().setLocale(browserLocale);
  }
}

function subscribeSettingsPersistence(): void {
  let last = JSON.stringify(useStore.getState().settings);
  useStore.subscribe((state) => {
    const next = JSON.stringify(state.settings);
    if (next !== last) {
      last = next;
      getLocalStore().set(SETTINGS_KEY, state.settings);
    }
  });
}

function mountReact(): void {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    logger.error('main.no-root', new Error('Element #root não encontrado'));
    return;
  }
  createRoot(rootEl).render(
    <StrictMode>
      <AppRoot />
    </StrictMode>,
  );
}

async function bootstrapCreature(): Promise<void> {
  const outcome = await loadSnapshot();
  let creature: Creature;
  let savedAt = Date.now();

  if (outcome.kind === 'loaded' && outcome.snapshot.creature) {
    creature = outcome.snapshot.creature as Creature;
    // Backfill defensivo para saves antigos (< V6.4 sem vitality)
    if (creature.state.vitality === undefined || creature.state.vitality === null) {
      creature = { ...creature, state: { ...creature.state, vitality: 100 } };
    }
    savedAt = outcome.snapshot.meta.savedAt;
    logger.info('save.loaded', { from: outcome.from, schemaMinor: outcome.snapshot.schemaMinor });
    // Hidrata lineage + npcs
    const snapAny = outcome.snapshot;
    if (snapAny.lineage) {
      useStore.getState().setLineage(snapAny.lineage as never);
    }
    if (snapAny.npcs && Array.isArray(snapAny.npcs)) {
      useStore.getState().setNpcs(snapAny.npcs as never);
    }
    if (snapAny.discoveredTypes && Array.isArray(snapAny.discoveredTypes)) {
      useStore.getState().setDiscoveredTypes(snapAny.discoveredTypes as string[]);
    }
    if (snapAny.world) {
      useStore.getState().setWorld(snapAny.world as never);
      if (snapAny.world.position) {
        useStore.getState().setPlayerPos(snapAny.world.position.x, snapAny.world.position.y);
      }
    }
    if (snapAny.combat) {
      const cs = snapAny.combat as { activeRun: unknown; totalRuns: number };
      // activeRun é descartado em U5 (não retoma run automaticamente)
      void cs.activeRun;
      // totalRuns hidrata
      if (typeof cs.totalRuns === 'number') {
        for (let i = 0; i < cs.totalRuns; i++) useStore.getState().incrementTotalRuns();
      }
    }
    // Detecta locale do save se não temos preferência salva
    if (!getLocalStore().get(SETTINGS_KEY) && snapAny.meta?.locale) {
      useStore.getState().setLocale(snapAny.meta.locale);
    }
  } else {
    if (outcome.kind === 'corrupted') {
      logger.warn('save.corrupted-resetting');
      useStore.getState().setBootError('save.corrupted');
    }
    creature = mkCreature({ genome: randomGenome(), id: uuid(), nowMs: Date.now() });
    logger.info('save.fresh', { creatureId: creature.id });
  }

  const lightOn = useStore.getState().lightOn;
  const caughtUp = catchUp(creature, savedAt, Date.now(), lightOn);
  useStore.getState().setActiveCreature(caughtUp);
}

function startPhaser(): void {
  const config: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    parent: 'game',
    width: window.innerWidth,
    height: window.innerHeight,
    backgroundColor: '#0a0a0a',
    pixelArt: true,
    scale: { mode: Phaser.Scale.RESIZE },
    scene: [BootScene],
  };
  new Phaser.Game(config);
}

hydrateSettingsFromLocal();
subscribeSettingsPersistence();
mountReact();
void bootstrapCreature().then(() => {
  startPhaser();
  getCareTickService().start();
  getNpcLifeService().start();
  getWorldService().assignNpcHomeBiomes();
  getWorldService().ensureItemsForToday();
  // Tick global do world (também faz vitality restore em casa)
  setInterval(() => getWorldService().tick(), 1000);
  // U6: catchup + tick de evolução do mundo
  const savedAtForCatchup = useStore.getState().creature?.lifecycle.bornAt ?? Date.now();
  getWorldEvolutionService().catchup(savedAtForCatchup, Date.now());
  getWorldEvolutionService().start();
  // U7: audio service
  getAudioService().start();
  // U8: cloud sync
  getCloudSyncService().start();
});
